import { Box, useMediaQuery } from "@material-ui/core";
import React, { useMemo } from "react";
import { StyledGrid, StyledP, Button } from "components/simple";
import {
  breakpoints,
  color,
  fontFamily,
  fontSize,
  spacingDefaults
} from "style/constants";
import { Link } from "react-router-dom";

type Banner = {
  id: string;
  title: string;
  body: string;
  primaryButton?: string;
  primaryButtonPath?: string;
  image?: string;
};

interface ContentfulBannerProps {
  banners: Banner[];
  dismissBanner: any;
}

export type BannerType =
  | "dashboard"
  | "my-journey"
  | "profile"
  | "assessment-overview"
  | "training"
  | "track-your-habits"
  | "resources"
  | "faq"
  | "brain-health-project"
  | "notification-preferences"
  | "privacy-policy";

const linkTo = (taskType: string) => {
  switch (taskType) {
    case "dashboard":
      return "/dashboard";
    case "my-journey":
      return "/myjourney";
    case "profile":
      return "/profile";
    case "assessment-overview":
      return "/assessment-overview";
    case "training":
      return "/training";
    case "track-your-habits":
      return "/track-your-habits";
    case "resources":
      return "/resources";
    case "faq":
      return "/faq";
    case "brain-health-project":
      return "/brain-health-project";
    case "notification-preferences":
      return "/notification_preferences";
    case "privacy-policy":
      return "/privacy-policy";
    default:
      return "/";
  }
};

const ContentfulBanner: React.FC<ContentfulBannerProps> = ({
  banners,
  dismissBanner
}) => {
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);

  // Memoize styles so they are not recreated on every render
  const containerCommon = useMemo(() => {
    return {
      backgroundColor: color.WHITE,
      alignContent: "center",
      alignItems: "center",
      marginBottom: spacingDefaults.large,
      margin: `0 auto ${spacingDefaults.large} auto`,
      flexDirection: "row",
      flexWrap: "nowrap"
    } as React.CSSProperties;
  }, []);

  const containerStyle = useMemo(() => {
    return {
      width: mobile ? "20rem" : "100%",
      maxWidth: mobile ? "20rem" : "50rem",
      gap: mobile ? spacingDefaults.xsmall : spacingDefaults.small
    } as React.CSSProperties;
  }, [mobile]);

  const imageStyle = useMemo(() => {
    return {
      width: mobile ? "7.5rem" : "10rem",
      height: mobile ? "5.75rem" : "10rem",
      flexShrink: 0,
      borderRadius: "4px",
      objectFit: "contain"
    } as React.CSSProperties;
  }, [mobile]);

  const textContainerStyle = useMemo(() => {
    return {
      display: "flex",
      width: mobile ? "11.75rem" : "auto",
      flex: "0 0 auto",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      gap: mobile ? spacingDefaults.xsmall : spacingDefaults.small,
      flexShrink: 0
    } as React.CSSProperties;
  }, [mobile]);

  const descriptionStyle = useMemo(() => {
    return {
      overflowWrap: "break-word",
      wordBreak: "break-word",
      maxWidth: mobile ? "none" : "25rem"
    } as React.CSSProperties;
  }, [mobile]);

  const mobileButtonsStyle = useMemo(() => {
    return {
      display: "flex",
      flexDirection: "row",
      gap: spacingDefaults.small,
      alignItems: "flex-start"
    } as React.CSSProperties;
  }, []);

  const webButtonsStyle = useMemo(() => {
    return {
      display: "flex",
      flexDirection: "column",
      gap: spacingDefaults.small,
      alignItems: "center",
      justifyContent: "center",
      marginLeft: "auto"
    } as React.CSSProperties;
  }, []);

  return (
    <>
      {banners.map((banner, idx) => {
        const key = `${banner.title || "banner"}-${idx}`;
        return (
          <StyledGrid
            key={key}
            container
            padding={mobile ? "16px" : spacingDefaults.normal}
            justifyContent={mobile ? "center" : "flex-start"}
            style={{ ...containerCommon, ...containerStyle }}
          >
            {/* Banner Image */}
            {banner.image && (
              <StyledGrid item>
                <img src={banner.image} alt={banner.title} style={imageStyle} />
              </StyledGrid>
            )}

            {/* Banner Text and Buttons */}
            <StyledGrid item style={textContainerStyle}>
              <Box
                fontSize={fontSize.medium}
                margin={`${spacingDefaults.xsmall} 0`}
                style={{ width: "100%" }}
              >
                {banner.title}
              </Box>
              {banner.body && (
                <StyledP
                  color={color.DARKERGRAY}
                  margin={`${spacingDefaults.xsmall} 0`}
                  fontSize={fontSize.small}
                  style={descriptionStyle}
                >
                  {banner.body}
                </StyledP>
              )}

              {/* Primary Button and Dismiss Button - Mobile Only */}
              {mobile && (
                <div style={mobileButtonsStyle}>
                  {banner.primaryButton && banner.primaryButtonPath && (
                    <Link to={linkTo(banner.primaryButtonPath)}>
                      <Button
                        aria-label={`Open ${banner.primaryButton}`}
                        style={{
                          height: "2.5rem",
                          padding: `${spacingDefaults.xsmall} ${spacingDefaults.normal}`,
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "5.25rem"
                        }}
                      >
                        {banner.primaryButton}
                      </Button>
                    </Link>
                  )}
                  <Button
                    inverted
                    aria-label="Dismiss banner"
                    onClick={() => {
                      dismissBanner(banner.id);
                    }}
                    style={{
                      height: "2.5rem",
                      padding: `${spacingDefaults.xsmall} ${spacingDefaults.normal}`,
                      fontSize: "1rem",
                      whiteSpace: "nowrap",
                      minWidth: "5.25rem"
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </StyledGrid>

            {/* Web View Buttons - Stacked on Right */}
            {!mobile && (
              <StyledGrid item style={webButtonsStyle}>
                {banner.primaryButton && banner.primaryButtonPath && (
                  <Link to={linkTo(banner.primaryButtonPath)}>
                    <Button
                      compressed
                      aria-label={`Open ${banner.primaryButton}`}
                      style={{
                        minWidth: "5.5rem",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {banner.primaryButton}
                    </Button>
                  </Link>
                )}
                <Button
                  inverted
                  compressed
                  onClick={() => {
                    dismissBanner(banner.id);
                  }}
                  aria-label="Dismiss banner"
                  style={{
                    minWidth: "5.5rem",
                    whiteSpace: "nowrap"
                  }}
                >
                  Dismiss
                </Button>
              </StyledGrid>
            )}
          </StyledGrid>
        );
      })}
    </>
  );
};

export default React.memo(ContentfulBanner);
